// src/ItemVariantsList.js ver 03 Dec 03 2025 9:30 pm - SB116
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './App';
import * as itemVariantsService from './api/itemVariantsService'; // Use new itemVariantsService
import * as rawItemsService from './api/rawItemsService'; // To get raw items for dropdown
import PaginationBar2 from './PaginationBar2';

function ItemVariantsList() {
    const [itemVariants, setItemVariants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const auth = useAuth();

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Filter States
    const [statusFilter, setStatusFilter] = useState('Active'); // Default to 'Active'
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const statusDropdownRef = useRef(null);

    const [associatedItemSeqIdFilter, setAssociatedItemSeqIdFilter] = useState('no-filter');
    const [associatedItemOptions, setAssociatedItemOptions] = useState([]); // For raw item dropdown
    const [isAssociatedItemDropdownOpen, setIsAssociatedItemDropdownOpen] = useState(false);
    const associatedItemDropdownRef = useRef(null);


    const TENANT_ID = auth.userProfile?.SubsCoDocId;

    useEffect(() => {
        const fetchItemVariants = async () => {
            if (!TENANT_ID) {
                setError("No associated company found. Please create one.");
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const token = await auth.getIdToken();
                if (!token) {
                    setError("Authentication required. Please log in.");
                    setLoading(false);
                    return;
                }

                // Fetch active raw items for the dropdown
                const rawItemsResponse = await rawItemsService.getRawItems(TENANT_ID, token, 1, 1000, 'Active'); // Fetch active raw items
                if (rawItemsResponse && rawItemsResponse.items) {
                    setAssociatedItemOptions(rawItemsResponse.items.map(item => ({
                        name: item.RawItemName,
                        seqId: item.SeqId,
                    })));
                } else {
                    console.warn("Could not fetch active raw items for filter dropdown.");
                }

                // Pass all filter parameters to the service call
                const response = await itemVariantsService.getItemVariants(
                    TENANT_ID,
                    token,
                    currentPage,
                    itemsPerPage,
                    statusFilter,
                    associatedItemSeqIdFilter
                );
                setItemVariants(response.itemVariants); // Note: response contains 'itemVariants'
                setTotalItems(response.totalItems);
                setTotalPages(response.totalPages);

                //close filter dropdown after response awaited
                setIsStatusDropdownOpen(false);
                setIsAssociatedItemDropdownOpen(false);

            } catch (err) {
                console.error("Error fetching item variants from Cloud Function: ", err);
                setError(`Failed to load item variants: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchItemVariants();
    }, [auth, TENANT_ID, currentPage, itemsPerPage, statusFilter, associatedItemSeqIdFilter]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleLimitChange = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1); // Reset to first page when limit changes
    };

    // Generic toggle for any dropdown
    const toggleDropdown = (setter) => {
        setter(prev => !prev);
    };

    // Filter change handlers
    const handleStatusFilterChange = (filter) => {
        setStatusFilter(filter);
        setCurrentPage(1);
        setIsStatusDropdownOpen(false);
    };

    const handleAssociatedItemFilterChange = (seqId) => { // NEW: Filter by SeqId
        setAssociatedItemSeqIdFilter(seqId);
        setCurrentPage(1);
        setIsAssociatedItemDropdownOpen(false);
    };


    // Close all dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setIsStatusDropdownOpen(false);
            }
            if (associatedItemDropdownRef.current && !associatedItemDropdownRef.current.contains(event.target)) {
                setIsAssociatedItemDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    // Helper to get raw item name from SeqId for display in header
    const getAssociatedItemNameFromSeqId = (seqId) => {
        const item = associatedItemOptions.find(opt => opt.seqId === seqId);
        return item ? item.name : 'Unknown Item';
    };


    if (loading) return <p>Loading item variants...</p>;
    if (error) return <p style={{ color: 'red' }}>Error: {error}</p>;
    if (!TENANT_ID) return <p style={{ color: 'orange' }}>Please create a company profile to manage item variants.</p>;


    return (
        <div>
            <div className="page-header">
                <h2>Item Variants</h2>
                {/* <Link to={`/item-variants/add/${TENANT_ID}`} className="btn secondary">Add New Item Variant</Link> */}
                <Link to={`/item-variants/add`} className="btn secondary">Add New Item Variant</Link>
            </div>

            <PaginationBar2
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
            />

            <table>
                <thead>
                    <tr>
                        <th>Seq ID</th>
                        <th
                            className="dropdown-th"
                            onClick={() => toggleDropdown(setIsAssociatedItemDropdownOpen)}
                            ref={associatedItemDropdownRef}
                            title="Click to Filter"
                        >
                            AssocItem {associatedItemSeqIdFilter !== 'no-filter' && `(${getAssociatedItemNameFromSeqId(associatedItemSeqIdFilter)})`}
                            {isAssociatedItemDropdownOpen && (
                                <ul className="dropdown-menu">
                                    <li onClick={() => handleAssociatedItemFilterChange('no-filter')}>&lt;no filter&gt;</li>
                                    {associatedItemOptions.map(item => (
                                        <li key={item.seqId} onClick={() => handleAssociatedItemFilterChange(item.seqId)}>{item.name}</li>
                                    ))}
                                </ul>
                            )}
                        </th>
                        <th>ItemId</th>
                        <th>Brand</th>
                        <th>Differentiator</th> {/* NEW */}
                        <th>Variant Name</th>
                        <th>ChefsRating</th>
                        <th>ChefsNotes</th> {/* NEW */}
                        <th
                            className="dropdown-th"
                            onClick={() => toggleDropdown(setIsStatusDropdownOpen)}
                            ref={statusDropdownRef}
                            title="Click to Filter"
                        >
                            Status {statusFilter !== 'no-filter' && `(${statusFilter})`}
                            {isStatusDropdownOpen && (
                                <ul className="dropdown-menu">
                                    <li onClick={() => handleStatusFilterChange('no-filter')}>&lt;no filter&gt;</li>
                                    <li onClick={() => handleStatusFilterChange('Active')}>Active</li>
                                    <li onClick={() => handleStatusFilterChange('Not-Active')}>Not-Active</li>
                                </ul>
                            )}
                        </th>
                        <th>Date Added</th>
                        <th>Date Last Modified</th>
                    </tr>
                </thead>
                <tbody>
                    {itemVariants.map(variant => (
                        <tr key={variant.id}>
                            <td>{variant.SeqId}</td>
                            <td>{variant.AssociatedItem}</td>
                            <td>{variant.AssociatedItemSeqId}</td>
                            <td>{variant.Brand}</td>
                           <td>{variant.Differentiator}</td> {/* NEW */}
                             <td>
                                {/* <Link to={`/item-variants/edit/${TENANT_ID}/${variant.id}`}> */}
                                <Link to={`/item-variants/edit/${variant.id}`}>
                                    {variant.ItemVariantName}
                                </Link>
                            </td>
                            <td>{variant.ChefsRating}</td> 
                            <td>{variant.ChefsNotes}</td> {/* NEW */}
                            <td>{variant.Status}</td>
                            <td>{variant.DateAdded ? new Date(variant.DateAdded._seconds * 1000).toLocaleString() : 'N/A'}</td>
                            <td>{variant.DateLastModified ? new Date(variant.DateLastModified._seconds * 1000).toLocaleString() : 'N/A'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <PaginationBar2
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
            />
        </div>
    );
}

export default ItemVariantsList;